/**
 * Abonnement Web Push côté navigateur.
 *
 * Commun aux deux applications. L'API expose la clé VAPID ; le service worker
 * affiche le bandeau. Sans permission, ou sans SW (développement), les helpers
 * restent silencieux.
 */

export interface PushClient {
  get<T>(path: string, options?: { cache?: boolean }): Promise<T>;
  post<T>(path: string, body?: unknown, options?: { queueOffline?: boolean }): Promise<T>;
}

export type PushSupport =
  | { ok: true }
  | { ok: false; reason: 'unsupported' | 'no-sw' | 'denied' | 'not-configured' | 'insecure' };

/** Le navigateur peut-il recevoir des notifications poussées ici ? */
export function pushSupport(): PushSupport {
  if (typeof window === 'undefined') return { ok: false, reason: 'unsupported' };
  if (!('Notification' in window) || !('PushManager' in window) || !('serviceWorker' in navigator)) {
    return { ok: false, reason: 'unsupported' };
  }
  // iOS n'autorise le push que pour une PWA installée (contexte sécurisé standalone
  // ou localhost). Hors HTTPS, PushManager échoue toujours.
  if (!window.isSecureContext) return { ok: false, reason: 'insecure' };
  if (Notification.permission === 'denied') return { ok: false, reason: 'denied' };
  return { ok: true };
}

/** Convertit la clé VAPID (base64 URL) en `Uint8Array` pour `PushManager`. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

async function readyRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    // `ready` attend qu'un SW contrôle la page — indispensable avant subscribe.
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

/**
 * Demande la permission (si besoin), s'abonne au PushManager, et enregistre
 * l'abonnement auprès de l'API. Idempotent : un second appel met à jour
 * l'endpoint existant.
 */
export async function enableWebPush(api: PushClient): Promise<PushSupport> {
  const support = pushSupport();
  if (!support.ok) return support;

  const { publicKey, configured } = await api.get<{ publicKey: string | null; configured: boolean }>(
    '/push/vapid-public-key',
    { cache: false },
  );
  if (!configured || !publicKey) return { ok: false, reason: 'not-configured' };

  if (Notification.permission === 'default') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, reason: 'denied' };
  }
  if (Notification.permission !== 'granted') return { ok: false, reason: 'denied' };

  const registration = await readyRegistration();
  if (!registration) return { ok: false, reason: 'no-sw' };

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });
  }

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    return { ok: false, reason: 'unsupported' };
  }

  await api.post(
    '/push/subscribe',
    {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    },
    { queueOffline: false },
  );

  return { ok: true };
}

/**
 * Réenregistre l'abonnement si la permission est déjà accordée (ex. après
 * login). Ne demande jamais de permission : silencieux si l'utilisateur n'a
 * pas encore accepté.
 */
export async function syncWebPushSubscription(api: PushClient): Promise<void> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const support = pushSupport();
  if (!support.ok) return;
  try {
    await enableWebPush(api);
  } catch (error) {
    console.warn('[push] synchronisation :', error);
  }
}

/** Coupe l'abonnement navigateur + côté serveur pour cet appareil. */
export async function disableWebPush(api: PushClient): Promise<void> {
  const registration = await readyRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  try {
    await api.post('/push/unsubscribe', { endpoint }, { queueOffline: false });
  } catch (error) {
    console.warn('[push] désabonnement serveur :', error);
  }
  await subscription.unsubscribe().catch(() => undefined);
}

export async function currentPushSubscription(): Promise<PushSubscription | null> {
  const registration = await readyRegistration();
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}
